import moment from "moment-timezone";
import { DATE_TIME_DISPLAY } from "../../../../common/dateTime";
import { notBeatingStartPriceLabel } from "../../../../packages/startPriceText";

// The values shown in the auction Excel and PDF exports. Both files are built
// from buildAuctionReport, so they always show the same names and numbers.

// Japanese and Dutch lots have no fixed end time: they run until a bidder accepts.
const OPEN_ENDED_FORMATS = ["JAPANESE_AUCTION", "DUTCH_AUCTION"];

// the app-wide display style (13/10/2026 04:12 PM), also in Excel and PDF
export const DATE_TIME_FORMAT = DATE_TIME_DISPLAY;

export const resolveZone = (zone) => zone || moment.tz.guess();

export const noEndText = (eventFormat) =>
  OPEN_ENDED_FORMATS.includes(eventFormat) ? "Open until accepted" : "–";

// Text for the page tables: "10/04/2025 15:45", or "–" when there is no date.
export const formatDateTime = (value, zone, eventFormat) =>
  value
    ? moment(value).tz(resolveZone(zone)).format(DATE_TIME_FORMAT)
    : noEndText(eventFormat);

export const hasLeadBid = (lot) => Number(lot.highestBid) > 0;

export const toNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const lotDisplayName = (buyer) =>
  `${buyer["users.firstName"] || ""} ${buyer["users.lastName"] || ""}`.trim();

// "(Post-bid)", or "(Prebid, Not below start price)" for a prebid that
// doesn't beat the start price in "YES (Consider the start price)"
export const offerNote = ({ stage, belowStartPrice }, eventType) => {
  const parts = [stage, belowStartPrice && notBeatingStartPriceLabel(eventType)];
  const text = parts.filter(Boolean).join(", ");
  return text ? `(${text})` : "";
};

const leadBidderText = (lot, eventType) =>
  [
    lot.highestBidder,
    offerNote(
      { stage: lot.leadStage, belowStartPrice: lot.leadBelowStartPrice },
      eventType
    ),
  ]
    .filter(Boolean)
    .join(" ");

export const rankPrefix = (eventType) =>
  eventType === "REVERSE_AUCTION" ? "L" : "H";

export const auctionTypeText = (eventType) =>
  eventType === "REVERSE_AUCTION" ? "Reverse auction" : "Forward auction";

export const auctionFormatText = (eventFormat) =>
  ({
    ENGLISH_AUCTION: "English",
    JAPANESE_AUCTION: "Japanese",
    DUTCH_AUCTION: "Dutch",
  }[eventFormat] || "English");

const createdByText = (user) => {
  if (!user) return "–";
  const name = `${user.firstName || ""} ${user.lastName || ""}`.trim();
  return user.email ? `${name} (${user.email})` : name || "–";
};

export const safeFilePart = (value) =>
  String(value || "auction").replace(/[^A-Za-z0-9_-]+/g, "-");

// kind tells each file how to show the value:
//   number, amount (2 decimals), date (24-hour, or "–"), text, longText (wraps)
const lotColumns = ({ eventData, zoneAbbr }) => {
  const currency = eventData.currency;
  const rank = rankPrefix(eventData.eventType);
  const stepName =
    eventData.eventType === "FORWARD_AUCTION" ? "Increment" : "Decrement";
  const stepUnit =
    eventData.incrementBidAmountBy === "PERCENTAGE" ? "%" : currency;

  return [
    { key: "sr", label: "Sr.", kind: "number" },
    { key: "lotNumber", label: "Lot No", kind: "text" },
    { key: "description", label: "Short Description", kind: "longText" },
    { key: "quantity", label: "Qty", kind: "number" },
    { key: "unit", label: "Unit", kind: "text" },
    { key: "startPrice", label: `Start Price (${currency})`, kind: "amount" },
    { key: "step", label: `${stepName} (${stepUnit})`, kind: "number" },
    { key: "startAt", label: `Start Date & Time (${zoneAbbr})`, kind: "date" },
    { key: "endAt", label: `End Date & Time (${zoneAbbr})`, kind: "date" },
    { key: "invited", label: "Invited bidders", kind: "number" },
    { key: "biddersBid", label: "Bidders bid", kind: "number" },
    { key: "bids", label: "No. of Bids", kind: "number" },
    { key: "leadBidder", label: `Lead Bidder (${rank}1)`, kind: "text" },
    { key: "leadBid", label: `Lead Bid (${currency})`, kind: "amount" },
    { key: "leadValue", label: `Lead Value (${currency})`, kind: "amount" },
    // left out when no lot of the auction has a prebid / post-bid
    ...(eventData.hasPrebids
      ? [
          {
            key: "bestPrebid",
            label: `Best Prebid (${currency})`,
            kind: "amount",
          },
        ]
      : []),
    ...(eventData.hasPostBids
      ? [
          {
            key: "bestPostBid",
            label: `Best Post-bid (${currency})`,
            kind: "amount",
          },
        ]
      : []),
    { key: "otherBids", label: "Other Bids", kind: "longText" },
  ];
};

export const buildAuctionReport = ({ eventData, lots, zone }) => {
  const timeZone = resolveZone(zone);
  const generatedAt = moment().tz(timeZone);
  const zoneAbbr = generatedAt.format("z");
  const rank = rankPrefix(eventData.eventType);

  const description = (eventData.description || "").trim();
  const lotsWithBid = lots.filter(hasLeadBid).length;
  const invited = toNumber(eventData.participants) || 0;
  const biddersWhoBid = toNumber(eventData.totalBuyers) || 0;

  let totalLeadValue = 0;
  const lotRows = lots.map((lot) => {
    const leadBid = hasLeadBid(lot);
    const leadValue = leadBid
      ? Number(lot.highestBid) * (Number(lot.quantity) || 1)
      : null;
    if (leadValue) totalLeadValue += leadValue;

    return {
      sr: lot.id,
      lotNumber: lot.lotNumber,
      description: lot.shortDescription,
      quantity: lot.quantity,
      unit: lot.unit,
      startPrice: lot.startPrice,
      step: lot.increment,
      startAt: lot.startAt,
      endAt: lot.endAt,
      invited: lot.participants,
      biddersBid: lot.totalBidders,
      bids: lot.totalBids,
      leadBidder: leadBid
        ? leadBidderText(lot, eventData.eventType)
        : "No bid",
      leadBid: leadBid ? lot.highestBid : null,
      leadValue,
      bestPrebid: lot.bestPrebid,
      bestPostBid: lot.bestPostBid,
      otherBids: (lot.buyers || [])
        .map((buyer, index) =>
          [
            `${lotDisplayName(buyer)} – ${buyer.amount} – ${rank}${
              buyer.rank ?? index + 2
            }`,
            offerNote(buyer, eventData.eventType),
          ]
            .filter(Boolean)
            .join(" ")
        )
        .join("\n"),
    };
  });

  return {
    timeZone,
    zoneAbbr,
    generatedText: `${generatedAt.format(DATE_TIME_FORMAT)} ${zoneAbbr}`,
    eventFormat: eventData.eventFormat,
    ownerOfLots: eventData.ownerOfLots || "–",
    auctionTitle: eventData.auctionTitle || "",
    // shown only when it adds something to the title
    description:
      description && description !== (eventData.auctionTitle || "").trim()
        ? description
        : null,
    typeText: `${auctionTypeText(eventData.eventType)} – ${auctionFormatText(
      eventData.eventFormat
    )}`,
    eventNumber: eventData.eventNumber,
    startAt: eventData.bidStartDateTime,
    endAt: eventData.bidEndDateTime,
    counts: {
      lots: lots.length,
      lotsWithBid,
      lotsNoBid: lots.length - lotsWithBid,
      invited,
      biddersWhoBid,
      invitedNoBid: invited - biddersWhoBid,
    },
    createdBy: createdByText(eventData.user),
    fileBase: `auction-${safeFilePart(eventData.eventNumber)}`,
    lotColumns: lotColumns({ eventData, zoneAbbr }),
    lotRows,
    totalLeadValue,
  };
};
